import * as React from 'react';

import { Button } from '@rematter/ui-web';
import { useAtom } from 'jotai';

import { PurchaseOrderMaterialsTable } from '../_shared/LineItems';

import { convertToPurchaseOrderLoadFormLineItem } from './helpers/convertPurchaseOrderToLoadLineItem';
import { usePurchaseOrderMaterialsErrors } from './hooks/usePurchaseOrderMaterialsErrors';

import {
  $addLineItem,
  $purchaseOrderForm,
  $removeLineItem,
  $updateLineItem,
} from '~/modules/trade/_state/purchaseOrderForm.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import {
  enumLoadWeightType,
  LoadFormLineItem,
} from '~/modules/trade/types/load';
import { PurchaseOrderLineItemRecord } from '~/modules/trade/types/purchaseOrder';

export const PurchaseOrderWizardStepMaterials = () => {
  const t = useTranslations();
  const [formState] = useAtom($purchaseOrderForm);
  const [, addLineItem] = useAtom($addLineItem);
  const [, updateLineItem] = useAtom($updateLineItem);
  const [, removeLineItem] = useAtom($removeLineItem);

  const formRef = React.useRef(formState);
  React.useEffect(() => {
    formRef.current = formState;
  }, [formState]);

  const handleAddMaterial = React.useCallback(() => {
    addLineItem();
  }, [addLineItem]);

  const handleUpdateLineItem = React.useCallback(
    (lineItemId: string, updates: Partial<LoadFormLineItem>) => {
      const index = formRef.current.lineItems.findIndex(
        (item) => item.id === lineItemId,
      );
      if (index === -1) return;

      const updatedItem: Partial<PurchaseOrderLineItemRecord> = {};

      if (updates.itemId !== undefined) {
        updatedItem.itemId = updates.itemId;
      }

      if (updates.weights?.[enumLoadWeightType.Net] != null) {
        updatedItem.quantity =
          updates.weights?.[enumLoadWeightType.Net]?.value ?? 0;
      }

      if (updates.unitPrice !== undefined && updates.unitPrice !== null) {
        updatedItem.unitPrice = updates.unitPrice;
      }

      if (updates.pricingUnit !== undefined && updates.pricingUnit !== null) {
        updatedItem.unit = updates.pricingUnit;
      }

      updateLineItem({ index, updates: updatedItem });
    },
    [updateLineItem],
  );

  const handleDeleteLineItem = React.useCallback(
    (lineItemId: string) => {
      const index = formRef.current.lineItems.findIndex(
        (item) => item.id === lineItemId,
      );
      if (index !== -1) {
        removeLineItem(index);
      }
    },
    [removeLineItem],
  );

  const loadFormLineItems = React.useMemo(
    () => formState.lineItems.map(convertToPurchaseOrderLoadFormLineItem),
    [formState.lineItems],
  );

  const materialsErrors = usePurchaseOrderMaterialsErrors(formState.errors);

  return (
    <div className="@PurchaseOrderWizardStepMaterials flex flex-col gap-6">
      {loadFormLineItems.length === 0 ? (
        <div className="py-12 text-center">
          <div className="mb-6 text-neutral/50">
            <p className="mb-2 text-lg">
              {t.purchaseOrdersView.wizard.materials.emptyState.title}
            </p>
            <p className="text-sm">
              {t.purchaseOrdersView.wizard.materials.emptyState.description}
            </p>
          </div>
          <Button
            variant="outline"
            color="primary"
            onPress={handleAddMaterial}
            size="large"
          >
            {t.purchaseOrdersView.wizard.materials.buttons.addMaterial}
          </Button>
        </div>
      ) : (
        <div>
          <div className="mb-4 flex justify-between">
            <h3 className="text-lg font-semibold">
              {t.itemTable.purchaseOrderTitle}
            </h3>
          </div>
          <PurchaseOrderMaterialsTable
            lineItems={loadFormLineItems}
            onUpdateLineItem={handleUpdateLineItem}
            onDeleteLineItem={handleDeleteLineItem}
            onAddLineItem={handleAddMaterial}
            disabled={formState.isSubmitting}
            errors={materialsErrors}
          />
        </div>
      )}
    </div>
  );
};
